/**
 * Deterministic, believable stand-in for a real AI suggestions service.
 *
 * Output is derived purely from the topic string (and audience), so:
 *  - the same topic always yields the same suggestions (stable tests, no flake),
 *  - different topics feel genuinely different - the topic is matched to a
 *    subject "category" (tech / food / games / finance / wellness / general),
 *    which selects real, named sources and tailored sections, and the topic
 *    text is interpolated into every name, tagline, voice sample and headline.
 *
 * A small artificial delay makes the UI exercise real loading states. The delay
 * is injectable so tests can run fast while still going through the async path.
 */
import { validateSubdomain } from '@nuff/core';

import type {
  IssuePreview,
  NameSuggestion,
  PreviewInput,
  SectionSuggestion,
  SourceGroup,
  SubdomainAvailability,
  SuggestionInput,
  SuggestionProvider,
  VoiceOption,
} from './provider';

/** Options for {@link createMockProvider}. */
export interface MockProviderOptions {
  /** Artificial latency per call, in ms. Defaults to 400. */
  readonly delayMs?: number;
}

const DEFAULT_DELAY_MS = 400;

function abortError(): DOMException {
  return new DOMException('The request was aborted', 'AbortError');
}

/** A cancellable delay - rejects with an AbortError if the signal fires. */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(abortError());
      },
      { once: true },
    );
  });
}

/** Small, stable string hash (FNV-1a) used to vary picks per topic. */
export function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Normalise a topic to Title Case for use inside generated copy. */
export function titleizeTopic(topic: string): string {
  const clean = topic.trim().replace(/\s+/g, ' ');
  if (clean.length === 0) {
    return 'your topic';
  }
  return clean
    .split(' ')
    .map((word) => (word.length <= 2 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

/**
 * Lowercase a topic for natural mid-sentence use, so a voice sample reads
 * "what's new in local news" rather than treating the topic as a proper noun
 * ("Local News"). Falls back to a placeholder for a blank topic.
 */
export function lowercaseTopic(topic: string): string {
  const clean = topic.trim().replace(/\s+/g, ' ');
  return clean.length === 0 ? 'your topic' : clean.toLowerCase();
}

/** Capitalise only the first letter of a string (for sentence starts). */
function capitalizeFirst(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

/** Generic words that make weak newsletter names, skipped when picking a keyword. */
const STOP_WORDS = new Set([
  'dev',
  'scene',
  'news',
  'weekly',
  'daily',
  'the',
  'and',
  'for',
  'of',
  'my',
  'your',
  'local',
  'personal',
  'guide',
  'tips',
  'world',
  'life',
  'stuff',
  'update',
  'updates',
  'today',
  'blog',
]);

/**
 * A short keyword handle for compact names: the longest salient (non-generic)
 * word, so "Indie game dev" → "Indie" and "Local food scene" → "Food" rather
 * than picking a throwaway tail word.
 */
function keyword(topic: string): string {
  const title = titleizeTopic(topic);
  const words = title.split(' ').filter((word) => word.length > 2);
  const salient = words.filter((word) => !STOP_WORDS.has(word.toLowerCase()));
  const pool = salient.length > 0 ? salient : words;
  if (pool.length === 0) {
    return title;
  }
  return pool.reduce((best, word) => (word.length > best.length ? word : best));
}

type CategoryKey = 'tech' | 'food' | 'games' | 'finance' | 'wellness' | 'general';

interface CategoryProfile {
  readonly key: CategoryKey;
  readonly patterns: readonly RegExp[];
}

// More specific subjects are matched before the broad "tech" bucket, so a topic
// like "indie game dev" reads as games rather than tech (dev).
const CATEGORY_PROFILES: readonly CategoryProfile[] = [
  {
    key: 'games',
    patterns: [/game/i, /gaming/i, /indie/i, /esport/i],
  },
  {
    key: 'food',
    patterns: [
      /food/i,
      /restaurant/i,
      /dining/i,
      /cook/i,
      /recipe/i,
      /coffee/i,
      /wine/i,
      /culinary/i,
    ],
  },
  {
    key: 'finance',
    patterns: [
      /financ/i,
      /crypto/i,
      /invest/i,
      /money/i,
      /stock/i,
      /startup/i,
      /market/i,
      /\bvc\b/i,
    ],
  },
  {
    key: 'wellness',
    patterns: [/health/i, /fitness/i, /wellness/i, /yoga/i, /mental/i, /nutrition/i, /mindful/i],
  },
  {
    key: 'tech',
    patterns: [
      /secur/i,
      /cloud/i,
      /\baws\b/i,
      /\bai\b/i,
      /software/i,
      /\bdev/i,
      /data/i,
      /tech/i,
      /cyber/i,
      /program/i,
      /\bcode/i,
    ],
  },
];

/** Match a topic to a subject category so different topics feel different. */
export function categoryFor(topic: string): CategoryKey {
  for (const profile of CATEGORY_PROFILES) {
    if (profile.patterns.some((pattern) => pattern.test(topic))) {
      return profile.key;
    }
  }
  return 'general';
}

/* ------------------------------------------------------------------ names -- */

function nameTemplates(input: SuggestionInput): NameSuggestion[] {
  const tt = titleizeTopic(input.topic);
  const kw = keyword(input.topic);
  // Audience colours a couple of taglines (not a uniform appended suffix).
  const forWhom = input.audience === 'company' ? 'your audience' : 'people like you';
  const crowd = input.audience === 'company' ? 'your customers' : 'fellow enthusiasts';
  return [
    { name: `The ${kw} Brief`, tagline: `The ${tt} stories that matter, in a five-minute read.` },
    { name: `${kw} Weekly`, tagline: `Everything worth knowing in ${tt}, once a week.` },
    {
      name: `The ${kw} Loop`,
      tagline: `Keep ${forWhom} in the loop on ${tt}, minus the doomscroll.`,
    },
    { name: `${kw} Field Notes`, tagline: `Hand-picked ${tt} finds for ${crowd}.` },
    { name: `Beyond the Feed`, tagline: `Smarter takes on ${tt}, minus the hype.` },
    { name: `${kw} Roundup`, tagline: `Your weekly briefing on ${tt}.` },
  ];
}

function buildNames(input: SuggestionInput): NameSuggestion[] {
  const templates = nameTemplates(input);
  const offset = hashString(input.topic) % templates.length;
  const rotated = [...templates.slice(offset), ...templates.slice(0, offset)];
  return rotated.slice(0, 5);
}

/* ----------------------------------------------------------------- voices -- */

function buildVoices(input: SuggestionInput): VoiceOption[] {
  // The topic reads as a common noun inside the sample, not a proper noun, so it
  // is lowercased mid-sentence and only sentence-initial for the openers.
  const topic = lowercaseTopic(input.topic);
  const topicStart = capitalizeFirst(topic);
  return [
    {
      id: 'punchy',
      label: 'Punchy and fun',
      sample: `${topicStart} moves fast. Here's the one thing you can't miss this week.`,
    },
    {
      id: 'warm',
      label: 'Warm and plain-spoken',
      sample: `Let's talk about what's new in ${topic}, no jargon, just the good stuff.`,
    },
    {
      id: 'sharp',
      label: 'Sharp and professional',
      sample: `This week in ${topic}: the developments that matter, and why.`,
    },
    {
      id: 'wry',
      label: 'Wry and irreverent',
      sample: `${topicStart}, minus the buzzwords and the breathless takes. You're welcome.`,
    },
  ];
}

/* --------------------------------------------------------------- sections -- */

interface SectionSeed {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly defaultSelected: boolean;
}

const CATEGORY_SECTIONS: Record<CategoryKey, readonly SectionSeed[]> = {
  tech: [
    {
      id: 'tool',
      title: 'Tool of the week',
      description: 'One tool or library worth a look.',
      defaultSelected: true,
    },
    {
      id: 'community',
      title: 'From the community',
      description: 'What people are building and debating.',
      defaultSelected: false,
    },
  ],
  food: [
    {
      id: 'recipe',
      title: 'Recipe of the week',
      description: 'Something to actually cook.',
      defaultSelected: true,
    },
    {
      id: 'spot',
      title: 'A spot to try',
      description: 'A place worth the trip.',
      defaultSelected: false,
    },
  ],
  games: [
    {
      id: 'pick',
      title: 'Game of the week',
      description: 'One release worth your time.',
      defaultSelected: true,
    },
    {
      id: 'community',
      title: 'From the community',
      description: 'Mods, clips and hot takes.',
      defaultSelected: false,
    },
  ],
  finance: [
    {
      id: 'chart',
      title: 'Chart of the week',
      description: 'One number that tells a story.',
      defaultSelected: true,
    },
    {
      id: 'moves',
      title: 'Market moves',
      description: 'Who did what, and why it matters.',
      defaultSelected: false,
    },
  ],
  wellness: [
    {
      id: 'habit',
      title: 'Habit of the week',
      description: 'One small change to try.',
      defaultSelected: true,
    },
    {
      id: 'expert',
      title: 'Ask an expert',
      description: 'A reader question, answered.',
      defaultSelected: false,
    },
  ],
  general: [
    {
      id: 'highlight',
      title: 'Highlight of the week',
      description: 'The one thing to remember.',
      defaultSelected: true,
    },
    {
      id: 'community',
      title: 'From the community',
      description: 'What readers are talking about.',
      defaultSelected: false,
    },
  ],
};

function buildSections(input: SuggestionInput): SectionSuggestion[] {
  const topicTitle = titleizeTopic(input.topic);
  const category = categoryFor(input.topic);
  const base: SectionSuggestion[] = [
    {
      id: 'news',
      title: `This week in ${topicTitle}`,
      description: 'The headlines, summarised.',
      defaultSelected: true,
    },
    {
      id: 'deepdive',
      title: 'Deep dive',
      description: 'One story, explained properly.',
      defaultSelected: true,
    },
  ];
  const extras = CATEGORY_SECTIONS[category];
  const tail: SectionSuggestion = {
    id: 'links',
    title: 'Quick links',
    description: 'Everything else worth a click.',
    defaultSelected: true,
  };
  return [...base, ...extras, tail];
}

/* ---------------------------------------------------------------- sources -- */

interface RawSource {
  readonly label: string;
  readonly url: string;
  readonly defaultSelected: boolean;
}

type SourceMap = Record<
  Exclude<CategoryKey, 'general'>,
  Record<'website' | 'youtube' | 'podcast' | 'reddit' | 'newsletter', readonly RawSource[]>
>;

const CATEGORY_SOURCES: SourceMap = {
  tech: {
    website: [
      { label: 'Hacker News', url: 'https://news.ycombinator.com', defaultSelected: true },
      { label: 'Ars Technica', url: 'https://arstechnica.com', defaultSelected: true },
    ],
    youtube: [{ label: 'Fireship', url: 'https://youtube.com/@Fireship', defaultSelected: true }],
    podcast: [
      { label: 'Darknet Diaries', url: 'https://darknetdiaries.com', defaultSelected: true },
    ],
    reddit: [
      { label: 'r/programming', url: 'https://reddit.com/r/programming', defaultSelected: true },
    ],
    newsletter: [{ label: 'TLDR', url: 'https://tldr.tech', defaultSelected: false }],
  },
  food: {
    website: [
      { label: 'Serious Eats', url: 'https://seriouseats.com', defaultSelected: true },
      { label: 'Eater', url: 'https://eater.com', defaultSelected: true },
    ],
    youtube: [
      { label: 'Bon Appétit', url: 'https://youtube.com/@bonappetit', defaultSelected: true },
    ],
    podcast: [
      { label: 'The Splendid Table', url: 'https://splendidtable.org', defaultSelected: true },
    ],
    reddit: [{ label: 'r/Cooking', url: 'https://reddit.com/r/Cooking', defaultSelected: true }],
    newsletter: [
      { label: 'Eater Newsletter', url: 'https://eater.com/newsletters', defaultSelected: false },
    ],
  },
  games: {
    website: [
      { label: 'Polygon', url: 'https://polygon.com', defaultSelected: true },
      { label: 'Rock Paper Shotgun', url: 'https://rockpapershotgun.com', defaultSelected: true },
    ],
    youtube: [
      { label: "Game Maker's Toolkit", url: 'https://youtube.com/@GMTK', defaultSelected: true },
    ],
    podcast: [
      {
        label: 'Triple Click',
        url: 'https://maximumfun.org/podcasts/triple-click',
        defaultSelected: true,
      },
    ],
    reddit: [
      { label: 'r/IndieGaming', url: 'https://reddit.com/r/IndieGaming', defaultSelected: true },
    ],
    newsletter: [{ label: 'Naavik Digest', url: 'https://naavik.co', defaultSelected: false }],
  },
  finance: {
    website: [
      { label: 'Bloomberg', url: 'https://bloomberg.com', defaultSelected: true },
      { label: 'Financial Times', url: 'https://ft.com', defaultSelected: true },
    ],
    youtube: [
      { label: 'Bloomberg Originals', url: 'https://youtube.com/@business', defaultSelected: true },
    ],
    podcast: [{ label: 'Planet Money', url: 'https://npr.org/planetmoney', defaultSelected: true }],
    reddit: [
      { label: 'r/investing', url: 'https://reddit.com/r/investing', defaultSelected: true },
    ],
    newsletter: [{ label: 'Morning Brew', url: 'https://morningbrew.com', defaultSelected: false }],
  },
  wellness: {
    website: [
      { label: 'Healthline', url: 'https://healthline.com', defaultSelected: true },
      { label: 'Well+Good', url: 'https://wellandgood.com', defaultSelected: true },
    ],
    youtube: [
      {
        label: 'Yoga With Adriene',
        url: 'https://youtube.com/@yogawithadriene',
        defaultSelected: true,
      },
    ],
    podcast: [{ label: 'Huberman Lab', url: 'https://hubermanlab.com', defaultSelected: true }],
    reddit: [{ label: 'r/Fitness', url: 'https://reddit.com/r/Fitness', defaultSelected: true }],
    newsletter: [
      {
        label: 'The Nutrition Insider',
        url: 'https://healthline.com/newsletter',
        defaultSelected: false,
      },
    ],
  },
};

const GROUP_LABELS: Record<'website' | 'youtube' | 'podcast' | 'reddit' | 'newsletter', string> = {
  website: 'Websites & blogs',
  youtube: 'YouTube',
  podcast: 'Podcasts',
  reddit: 'Reddit & forums',
  newsletter: 'Newsletters',
};

const GROUP_ORDER = ['website', 'youtube', 'podcast', 'reddit', 'newsletter'] as const;

function derivedSourcesFor(
  kind: 'website' | 'youtube' | 'podcast' | 'reddit' | 'newsletter',
  topic: string,
): RawSource[] {
  const topicTitle = titleizeTopic(topic);
  const query = encodeURIComponent(topic.trim());
  switch (kind) {
    case 'website':
      return [
        {
          label: `${topicTitle} on Google News`,
          url: `https://news.google.com/search?q=${query}`,
          defaultSelected: true,
        },
      ];
    case 'youtube':
      return [
        {
          label: `${topicTitle} on YouTube`,
          url: `https://youtube.com/results?search_query=${query}`,
          defaultSelected: false,
        },
      ];
    case 'podcast':
      return [
        {
          label: `${topicTitle} on Apple Podcasts`,
          url: `https://podcasts.apple.com/search?term=${query}`,
          defaultSelected: false,
        },
      ];
    case 'reddit':
      // A search link, not a fabricated "r/<slug>" subreddit - and left
      // unchecked, since we can't vouch that a matching community exists.
      return [
        {
          label: `Search Reddit for “${topicTitle}”`,
          url: `https://reddit.com/search/?q=${query}`,
          defaultSelected: false,
        },
      ];
    case 'newsletter':
      return [
        {
          label: `${topicTitle} on Substack`,
          url: `https://substack.com/search/${query}`,
          defaultSelected: false,
        },
      ];
  }
}

function buildSources(input: SuggestionInput): SourceGroup[] {
  const category = categoryFor(input.topic);
  return GROUP_ORDER.map((kind) => {
    const named = category === 'general' ? [] : CATEGORY_SOURCES[category][kind];
    const derived = derivedSourcesFor(kind, input.topic);
    // Topic-derived source first so every group is anchored to the topic.
    const raw = [...derived, ...named];
    return {
      kind,
      label: GROUP_LABELS[kind],
      sources: raw.map((source, index) => ({
        id: `${kind}:${index}`,
        label: source.label,
        kind,
        url: source.url,
        defaultSelected: source.defaultSelected,
      })),
    };
  });
}

/* ---------------------------------------------------------------- preview -- */

function buildPreview(input: PreviewInput): IssuePreview {
  const topicTitle = titleizeTopic(input.topic);
  const titles =
    input.sectionTitles.length > 0 ? input.sectionTitles : [`This week in ${topicTitle}`];
  // Headlines are derived from the section's own title so the preview reads
  // coherently - each blurb sits under a headline that belongs to that section.
  const sections = titles.slice(0, 4).map((title) => ({
    title,
    headline: `Inside “${title}”`,
    blurb: `A taste of what “${title}” brings you each issue on ${topicTitle}.`,
  }));
  const intro =
    input.voice !== undefined
      ? input.voice.sample
      : `Your first issue of ${input.name || topicTitle}, all about ${topicTitle}.`;
  return {
    subject: `${input.name || topicTitle}: your first issue on ${topicTitle}`,
    intro,
    sections,
  };
}

/* ------------------------------------------------------------- subdomain -- */

// A tiny blocklist so the UI can exercise a real "Taken" state. Everything else
// that passes validation reads as available; the real endpoint drops in later.
const TAKEN_SUBDOMAINS: ReadonlySet<string> = new Set(['taken', 'demo', 'acme', 'hello']);

function checkAvailability(name: string): SubdomainAvailability {
  const result = validateSubdomain(name);
  if (!result.ok) {
    return { available: false, reason: result.reason };
  }
  if (TAKEN_SUBDOMAINS.has(result.value)) {
    return { available: false, reason: 'That address is already taken.' };
  }
  return { available: true };
}

/* --------------------------------------------------------------- provider -- */

/** Build a mock provider. Inject a small `delayMs` in tests. */
export function createMockProvider(options: MockProviderOptions = {}): SuggestionProvider {
  const delayMs = options.delayMs ?? DEFAULT_DELAY_MS;
  return {
    async suggestNames(input, requestOptions) {
      await delay(delayMs, requestOptions?.signal);
      return buildNames(input);
    },
    async suggestVoices(input, requestOptions) {
      await delay(delayMs, requestOptions?.signal);
      return buildVoices(input);
    },
    async checkSubdomain(name, requestOptions) {
      await delay(delayMs, requestOptions?.signal);
      return checkAvailability(name);
    },
    async suggestSections(input, requestOptions) {
      await delay(delayMs, requestOptions?.signal);
      return buildSections(input);
    },
    async suggestSources(input, requestOptions) {
      await delay(delayMs, requestOptions?.signal);
      return buildSources(input);
    },
    async previewIssue(input, requestOptions) {
      await delay(delayMs, requestOptions?.signal);
      return buildPreview(input);
    },
  };
}

/** Default provider used by the app (400ms latency to show loading states). */
export const mockSuggestionProvider: SuggestionProvider = createMockProvider();
